import React from 'react';
import { Box, Typography, Container, Button, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Calendar, Target } from 'lucide-react';

const ACCENT = '#6B21A8';
const TEXT_DARK = '#0F0F0F';
const TEXT_MUTED = '#475569';

const AboutSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 24, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <Box
      id="about"
      sx={{
        pt: { xs: 8, md: 10 },
        pb: { xs: 2, md: 3 },
        background: '#fff',
        position: 'relative',
        overflow: { xs: 'visible', md: 'hidden' },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: { xs: 4, lg: 6 },
              alignItems: 'center',
            }}
          >
            {/* Left: Image + floating overlay card */}
            <motion.div
              variants={itemVariants}
              whileHover={{ rotate: -3, scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              style={{ perspective: '1000px', transformOrigin: 'center center' }}
            >
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.12)',
                }}
              >
                <Box
                  component="img"
                  src="/about1.jpg"
                  alt="Reviving India"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    minHeight: { xs: 280, sm: 360 },
                    objectFit: 'cover',
                  }}
                />
                {/* Floating overlay card - bottom right on desktop, inline on mobile */}
                <motion.div
                  whileHover={{ rotate: 3, scale: 1.03 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                <Box
                  sx={{
                    position: { xs: 'relative', lg: 'absolute' },
                    bottom: { xs: 'auto', lg: 24 },
                    right: { xs: 'auto', lg: 24 },
                    left: { xs: 'auto', lg: 'auto' },
                    mt: { xs: 2, lg: 0 },
                    mx: { xs: 0, lg: 0 },
                    p: 2,
                    borderRadius: '16px',
                    background: '#FFFFFF',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    maxWidth: { xs: '100%', lg: 220 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <MapPin size={18} color={ACCENT} />
                    <Typography
                      sx={{
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        color: TEXT_DARK,
                        letterSpacing: '0.02em',
                      }}
                    >
                      India
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: TEXT_MUTED,
                      lineHeight: 1.5,
                      fontStyle: 'italic',
                    }}
                  >
                    Empowering youth through technology & innovation
                  </Typography>
                </Box>
                </motion.div>
              </Box>
            </motion.div>

            {/* Right: Content */}
            <motion.div variants={itemVariants}>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.2em',
                  color: ACCENT,
                  textTransform: 'uppercase',
                  mb: 1.5,
                }}
              >
                Our Story
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: { xs: '2.25rem', md: '2.75rem' },
                  fontWeight: 600,
                  color: TEXT_DARK,
                  lineHeight: 1.2,
                  mb: 3,
                }}
              >
                About{' '}
                <Box component="span" sx={{ color: ACCENT }}>
                  Us
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '1rem',
                  color: TEXT_MUTED,
                  lineHeight: 1.8,
                  mb: 2,
                }}
              >
                RevIn was established in 2013 (as &quot;Rock In Robos&quot;) and formalized as Reviving India in 2016.
                We are a youth club focused on the benefit and upliftment of students who want to learn and
                apply their skills for the best output.
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '1rem',
                  color: TEXT_MUTED,
                  lineHeight: 1.8,
                  mb: 2,
                }}
              >
                We aspire to spread the principle &quot;Think what you Do & Do what you think&quot; and serve the youth.
                REVIN INNOVATION creates an open platform where students can share ideas, get guidance from
                experts, and implement solutions to real-world challenges.
              </Typography>

              {/* Bottom info row: Mission | Founded | Read More */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: { xs: 2, sm: 3 },
                  mt: 3,
                  pt: 3,
                  borderTop: '1px solid rgba(0,0,0,0.08)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Target size={20} color={ACCENT} />
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: TEXT_DARK }}>
                    Mission
                  </Typography>
                  <Typography sx={{ fontSize: '0.9375rem', color: TEXT_MUTED }}>
                    Empower youth
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Calendar size={20} color={ACCENT} />
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: TEXT_DARK }}>
                    Founded
                  </Typography>
                  <Typography sx={{ fontSize: '0.9375rem', color: TEXT_MUTED }}>
                    2013
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  endIcon={<ArrowRight size={18} />}
                  onClick={() => {
                    const el = document.getElementById('about');
                    el?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    color: ACCENT,
                    borderColor: ACCENT,
                    borderRadius: '12px',
                    px: 2.5,
                    py: 1.25,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: ACCENT,
                      backgroundColor: 'rgba(107, 33, 168, 0.08)',
                    },
                  }}
                >
                  Read More
                </Button>
              </Box>
            </motion.div>
          </Box>

          {/* Stats row - horizontal strip with dividers */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'stretch',
                justifyContent: 'center',
                mt: 4,
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid rgba(0,0,0,0.06)',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
                background: '#FFFFFF',
              }}
            >
              {[
                { label: 'Established', value: '2013' },
                { label: 'Youth Club', value: 'For Students' },
                { label: 'Innovation', value: 'Open Platform' },
                { label: 'Mission', value: 'Empower Youth' },
              ].map((stat, index) => (
                <React.Fragment key={stat.label}>
                  {index > 0 && (
                    <Box
                      sx={{
                        width: '1px',
                        alignSelf: 'stretch',
                        bgcolor: 'rgba(0,0,0,0.08)',
                        display: { xs: 'none', sm: 'block' },
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      flex: { xs: '1 1 50%', sm: '1 1 0' },
                      minWidth: { xs: 120, sm: 0 },
                      py: 2.5,
                      px: { xs: 2, sm: 3 },
                      textAlign: 'center',
                      borderLeft: { xs: 'none', sm: index === 0 ? 'none' : 'none' },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: 600,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        color: ACCENT,
                        lineHeight: 1.2,
                        letterSpacing: '0.02em',
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: TEXT_MUTED,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        mt: 0.5,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          </motion.div>

          {/* Learn With Us - heading + two images */}
          <motion.div variants={itemVariants}>
            <Box sx={{ mt: 2, mb: 0 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.2em',
                    color: ACCENT,
                    textTransform: 'uppercase',
                    mb: 1.5,
                  }}
                >
                  What&apos;s Next
                </Typography>
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    fontWeight: 600,
                    color: TEXT_DARK,
                    lineHeight: 1.2,
                  }}
                >
                  Learn With{' '}
                  <Box component="span" sx={{ color: ACCENT }}>
                    Us
                  </Box>
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                  maxWidth: 900,
                  mx: 'auto',
                }}
              >
                <motion.div
                  initial={{ rotate: isMobile ? 0 : -3 }}
                  whileInView={{ rotate: isMobile ? 0 : -3 }}
                  whileHover={isMobile ? undefined : { rotate: -5, scale: 1.03 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  viewport={{ once: true }}
                >
                  <Box
                    sx={{
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
                      border: '1px solid rgba(0,0,0,0.06)',
                      aspectRatio: '4/3',
                    }}
                  >
                    <Box
                      component="img"
                      src="/learn-with-us-1.jpg"
                      alt="Reviving India – Learn with us"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </Box>
                </motion.div>
                <motion.div
                  initial={{ rotate: isMobile ? 0 : 3 }}
                  whileInView={{ rotate: isMobile ? 0 : 3 }}
                  whileHover={isMobile ? undefined : { rotate: 5, scale: 1.03 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  viewport={{ once: true }}
                >
                  <Box
                    sx={{
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
                      border: '1px solid rgba(0,0,0,0.06)',
                      aspectRatio: '4/3',
                    }}
                  >
                    <Box
                      component="img"
                      src="/learn-with-us-2.jpg"
                      alt="Reviving India – NAAC visit"
                      onError={(e) => {
                        const t = e.target as HTMLImageElement;
                        if (t && t.src !== '/learn-with-us-1.jpg') {
                          t.src = '/learn-with-us-1.jpg';
                          t.onerror = null;
                        }
                      }}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </Box>
                </motion.div>
              </Box>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AboutSection;
